import { Label, LabelAssociation } from '@provision/entity-types';
import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { useNotifications } from 'src/utils/hooks';

export const CREATE_LABEL_ASSOCIATION_MUTATION = gql`
  mutation CreateLabelAssociation($data: LabelAssociationDataInput) {
    createLabelAssociation(data: $data) {
      id
      labelAssociationId
      partnerId
      labelId
      entity
    }
  }
`;

export const useLabelAssociation = () => {
  const { showErrorAlert } = useNotifications();

  const [createLabelAssociationMutation] = useMutation<{
    createLabelAssociation: Partial<LabelAssociation>[];
  }>(CREATE_LABEL_ASSOCIATION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong creating a new label association, please try again',
      );
    },
  });

  const createLabelAssociation = useCallback(
    async ({
      label,
      partnerId,
      entity,
    }: {
      label: Label;
      partnerId?: number | null;
      entity: string;
    }) => {
      if (!label) {
        return;
      }

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createLabelAssociationMutation({
        variables: {
          data: {
            labelId: label.labelId,
            partnerId,
            entity,
          },
        },
        optimisticResponse: {
          createLabelAssociation: [
            {
              labelAssociationId: tempId,
              id: tempId,
              labelId: label.labelId || tempId,
              partnerId: partnerId ?? label.partnerId,
              entity: label.displayName ?? '',
              createdAt,
            },
          ],
        },
      });

      return data?.createLabelAssociation;
    },
    [createLabelAssociationMutation],
  );

  return {
    createLabelAssociation,
  };
};
